import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import ModalSubHeader from '@components/ModalComponents/ModalSubHeader'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { BrandGroupMemberModel } from '@models/types'

import useCreateBrandGroupPublicationModal from './hooks/useCreateBrandGroupPublicationModal'

import SelectBrandGroup from './components/SelectBrandGroup'
import SelectPublication from './components/SelectPublication'

type ModalContextTypes = {
  modalData: {
    brandGroupPublication: Partial<BrandGroupMemberModel>,
  },
  topModalKey: string,
}

type ModalCallbacks = {
  closeModal: () => void,
  createBrandGroupPublication: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateBrandGroupPublicationModal = (props: ModalProps) => {
  const { callbacks, modalKey = 'CreateBrandGroupPublicationModal', showModal } = props
  const { closeModal, createBrandGroupPublication } = callbacks || {}

  const modalContext = useContext<ModalContextTypes>(ModalContext)
  const { modalData, topModalKey } = modalContext

  const isTopModal = topModalKey === modalKey

  const modalPayload = digObject(modalData, modalKey, {})
  const { brandGroupPublication } = modalPayload

  const modalHookPayload = useCreateBrandGroupPublicationModal({ brandGroupPublication, closeModal })

  const {
    brandGroup,
    brandGroupPublicationFormPayload: { saveEnabled },
    createBrandGroupPublicationPayload,
    creating,
    entityText,
  } = modalHookPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Add" titleSecondLine={entityText} />

      <ModalSubHeader title="Assign a Publication to" ownerTitle={brandGroup.title || ''} />

      <SidebarModal.Content>
        <Form>
          {!!brandGroupPublication.brand_group_id && (
            <SelectPublication isTopModal={isTopModal} modalHookPayload={modalHookPayload} />
          )}

          {!!brandGroupPublication.publication_id && (
            <SelectBrandGroup isTopModal={isTopModal} modalHookPayload={modalHookPayload} />
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          // @ts-expect-error TS2345
          onClick={() => createBrandGroupPublication(createBrandGroupPublicationPayload)}
          size="large"
        >
          {saveEnabled ? `Add ${entityText}` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateBrandGroupPublicationModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
